import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Accent, BackgroundColor, InverseColor, OpacityColor, TextColor } from 'styles/Global/colorSemantic';
import { zIndex } from 'styles/Global/globalStyles';
import { Body5Medium, Caption1MediumButton } from 'styles/Global/typography';
import { Radius } from 'styles/Global/objectStyles';
import IcInfoFill from 'assets/Icons/Check/IcInfoFill';
import IcErrorFill from 'assets/Icons/Check/IcErrorFill';
import IconCheckRoundFill from 'assets/Icons/Check/IconCheckRoundFill';

type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
    message: string;
    buttonMessage?: string;
    type: ToastType;
    visible: boolean;
    onClose: () => void;
    duration?: number;
    onClick?: () => void;
}

const SnackBar = ({ message, type, visible, onClose, duration = 4000, buttonMessage, onClick }: ToastProps) => {
    // const SnackBar = ({ message, type, visible, onClose, duration = 4000 }: ToastProps) => {
    useEffect(() => {
        if (visible) {
            const timer = setTimeout(() => {
                onClose();
            }, duration);

            // localstorage에 snackbar가 true일 때만 snackbar가 보이도록 설정, navigate로 인해 데이터를 넘기면 데이터를 메모리에서 계속 들고있기 때문에 새로고침 시에도 계속해서 snackbar가 보이게 되어 만든 로직
            setTimeout(() => {
                localStorage.setItem('snackbar', 'false');
            }, duration + 500);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [visible, onClose, duration]);

    return (
        <ToastContainer $visible={visible} $type={type}>
            <MessageWrapper>
                {type === 'success' && <IconCheckRoundFill $width={16} $height={16} $color={Accent.color_accent_cyan_strong} />}
                {type === 'error' && <IcInfoFill />}
                {type === 'info' && <IcErrorFill />}
                <ToastMessage>{message}</ToastMessage>
            </MessageWrapper>
            {buttonMessage && <ToastButton onClick={onClick}>{buttonMessage}</ToastButton>}
        </ToastContainer>
    );
};

export default SnackBar;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ $visible: boolean; $type: ToastType }>`
    position: fixed;
    display: flex;
    align-items: center;
    justify-content: space-between;
    bottom: 16px;
    width: 90%;
    height: 48px;
    padding: 16px;
    border-radius: ${Radius.radius_8};
    z-index: ${zIndex.toast};
    border-radius: 99px;
    background-color: ${BackgroundColor.color_background_darkgray_normal};

    animation: ${({ $visible }) => ($visible ? slideUp : slideDown)} 0.3s forwards;
`;

const MessageWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ToastMessage = styled(Body5Medium)`
    color: ${OpacityColor.color_opacity_white_80};
    text-align: center;
    word-break: break-word;
`;

const ToastButton = styled(Caption1MediumButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    background-color: ${BackgroundColor.color_background_dim_heavy};
    border-radius: ${Radius.radius_circle};
    color: ${InverseColor.color_inverse_white};
`;
